import { put, select, fork, all } from 'redux-saga/effects'
import * as apiActions from 'actions/apiActions'

/*
 | This is meant to monitor data returned from the API prior to going back to the redux store.
 | If that data is a dependency that was used to create some other data that is now stale, this
 | can signal to also fetch the new dependant data. For example, injectionDue data is dependant
 | on the previous injection. If an Injection api call has been complete, we can see if injectionDue
 | data is present anywhere that should be updated as a side effect.
 |
 | BE VERY CAREFUL ABOUT RECURSION HERE! it would be easy to make one api call update a second
 | and then make the second update the first, on and on forever.
*/

/*
 |  Data manager lists out all of the resources (that anything cares about so far) that can be contacted
 |  by the api. the cases of the switch call the individual functional monitors that handle looking for
 |  possible dependant data and making calls to replaces as needed.
 */
export default function * dataManager(action) {
  const { resource } = action
  switch (resource.replace('_PRE_API', '').replace('_POST_API', '')) {
    case 'PATIENT_INJECTION':
    case 'PATIENT_PRESCRIPTION_INJECTION':
      yield fork(standardInjectionWindow, action)
      yield fork(injectionGraphWindow, action)
      break
    case 'PATIENT_PRESCRIPTION_TREATMENT_SET':
      yield fork(orderEntryWindow, action)
      break
    case 'PROFILE':
      yield fork(profileWindow, action)
      break
    case 'STATE_MACHINE':
      yield fork(updateAppConfigStates, action)
      break
    default:
  }
}

function getResultAsArray(action) {
  let { result } = action
  if (!Array.isArray(result)) {
    result = [result]
  }
  return [...result]
}
function * updateAppConfigStates(action) {
  if (
    (action.method === 'CREATE' || action.method === 'DELETE') &&
    action.resource === 'STATE_MACHINE_POST_API'
  ) {
    // if we created or deleted a state machine we need to read all of the states in the system again.
    yield put(apiActions.xtractAPI('state', 'READ_ALL', null, null))
  }
}

function * standardInjectionWindow(action) {
  // get the current injectionsDue from the standardInjectionWindow part of redux
  const injectionWindowInjectionsDue = yield select(state => {
    const { patientDisplay = {} } = state
    const { standardInjectionWindow } = patientDisplay
    return standardInjectionWindow == null
      ? null
      : standardInjectionWindow.injectionsDue
  })
  if (
    injectionWindowInjectionsDue &&
    (action.method === 'CREATE' ||
      action.method === 'UPDATE' ||
      action.method === 'DELETE')
  ) {
    const patientId = action.ids.patient_id
    const result = getResultAsArray(action)
    switch (action.resource) {
      case 'PATIENT_INJECTION_PRE_API':
      case 'PATIENT_PRESCRIPTION_INJECTION_PRE_API':
        const calls = []
        result.forEach(inj => {
          // only set pending for calls with an injection_id (excludes validation)
          if (inj && inj.injection_id) {
            calls.push(
              put({
                type: 'SET_PENDING_INJECTION_STATE',
                state: 'waiting',
                injectionId: inj.injection_id
              })
            )
          }
        })
        if (calls.length != 0) {
          yield all(calls)
        }
        break
      case 'PATIENT_INJECTION_POST_API':
      case 'PATIENT_PRESCRIPTION_INJECTION_POST_API':
        yield all(
          result.map(inj =>
            put({
              type: 'XTRACT_API_CALL',
              resource: `patient/${patientId}/prescription/${inj.prescription_id}/injection_due`,
              method: 'READ',
              obj: null,
              callbacks: null
            })
          )
        )
        break
      default:
        break
    }
  }
}

function * injectionGraphWindow(action) {
  // get the current charts from the injectionGraphWindow part of redux
  const graphWindowCharts = yield select(state => {
    const { patientDisplay = {} } = state
    const { injectionGraphWindow } = patientDisplay
    return injectionGraphWindow == null ? null : injectionGraphWindow.charts
  })
  if (graphWindowCharts != null) {
    switch (action.resource) {
      case 'PATIENT_INJECTION_POST_API':
      case 'PATIENT_PRESCRIPTION_INJECTION_POST_API':
        const patientId = action.ids.patient_id
        const prescriptionId = action.ids.prescription_id
        yield put(
          apiActions.xtractAPI(
            `patient/${patientId}/prescription/${prescriptionId}/injection_plan`,
            'READ',
            null,
            null
          )
        )
        break
      default:
        break
    }
  }
}

function * orderEntryWindow(action) {
  // get the current orders from the orderEntryWindow part of redux
  const orders = yield select(state => {
    const { patientDisplay = {} } = state
    const { orderEntryWindow } = patientDisplay
    return orderEntryWindow == null ? null : orderEntryWindow.orders
  })
  if (
    orders &&
    (action.method === 'CREATE' ||
      action.method === 'UPDATE' ||
      action.method === 'DELETE')
  ) {
    const patientId = action.ids.patient_id
    switch (action.resource) {
      case 'PATIENT_PRESCRIPTION_TREATMENT_SET_POST_API':
        yield put(
          apiActions.xtractAPI(
            `patient/${patientId}/treatment_set`,
            'READ',
            null,
            null
          )
        )
        break
      default:
        break
    }
  }
}

function * profileWindow(action) {
  // get the current profiles from the profileWindow part of redux
  const profiles = yield select(state => {
    const { adminDisplay = {} } = state
    const { profileWindow } = adminDisplay
    return profileWindow == null ? null : profileWindow.profiles
  })
  if (
    profiles &&
    (action.method === 'CREATE' ||
      action.method === 'UPDATE' ||
      action.method === 'DELETE')
  ) {
    switch (action.resource) {
      case 'PROFILE_POST_API':
        yield put(apiActions.xtractAPI('provider', 'READ', null, null))
        break
      default:
        break
    }
  }
}
